import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'formik';
import { useSelector } from 'react-redux';
import { Button, Col, FormGroup, FormLabel, Row } from 'react-bootstrap';
import {
  FormInputField,
  FormSelectField,
  FormCheckboxField,
} from '../../common/components/formFields';
import { constantToSelect, dataToSelect } from '../../common/helpers';
import { DealerUserStatusName, UserStatus } from '../../constants/UserStatus';
import FormGroupCheckboxField from '../../common/components/formFields/FormGroupCheckboxField';
import { EmployeeUserRoleName, DealerUserRoleName, UserRole } from '../../constants/UserRole';
import { UserPositionName } from '../../constants/UserPosition';
import ComponentCard from '../../common/components/cards/ComponentCard';

const dealerUserPositions = constantToSelect(UserPositionName);

function UserForm({ locations, organizationRole, isOwner, values, organizationStatus }) {
  const [dealerUserStatuses, setDealerUserStatuses] = useState([]);
  const locationsData = dataToSelect(locations, ['id', 'address']);
  const userDetails = useSelector((state) => state.users.get('userDetails'));
  const userStatus = useSelector((state) => state.users.get('userDetails')?.user?.status);
  let dealerUserRoles = constantToSelect(isOwner ? DealerUserRoleName : EmployeeUserRoleName);

  useEffect(() => {
    let statuses = [];
    if (userStatus) {
      switch (userStatus) {
        case UserStatus.PENDING:
          statuses = [UserStatus.ACTIVE, UserStatus.REJECTED];
          break;
        case UserStatus.ACTIVE:
          statuses = [UserStatus.BLOCKED];
          break;
        case UserStatus.BLOCKED:
          statuses = [UserStatus.ACTIVE];
          break;
        case UserStatus.BLOCKED_DUE_TO_PAYMENT:
          setDealerUserStatuses([]);
          return;
        default:
          statuses = [];
      }
    }

    statuses = Object.fromEntries(
      [...statuses].map((item) => [item, DealerUserStatusName[item]]),
    );

    setDealerUserStatuses(constantToSelect(statuses));
  }, [userStatus]);

  const checkCheckboxDependencies = (value, remove) => {
    if (!value.includes(UserRole.SELLER) && value.includes(UserRole.TRANSACTION_MANAGEMENT)) {
      const idx = value.indexOf(UserRole.TRANSACTION_MANAGEMENT);
      if (idx !== -1) {
        remove(idx);
      }
    }
  };

  if (!isOwner && organizationRole) {
    dealerUserRoles = dealerUserRoles.map((role) => {
      const disabled =
        (role.value === UserRole.BUYER && !organizationRole.isBuyer) ||
        (role.value === UserRole.SELLER && !organizationRole.isSeller) ||
        (role.value === UserRole.TRANSACTION_MANAGEMENT &&
          (!organizationRole.isSeller || !values.role.includes(UserRole.SELLER)));
      return { ...role, disabled };
    });
  }

  return (
    <Form autoComplete="off">
      <ComponentCard title="Edit User" isCollapsible>
        {userDetails?.user?.isOwner && (
          <Row>
            <Col md="12" className="d-flex flex-column align-items-center">
              <p className="fw-bold mb-4">Changes affect the dealer&apos;s account</p>
              {userStatus === UserStatus.BLOCKED_DUE_TO_PAYMENT && (
                <p className="fw-bold">Blocked due to payment</p>
              )}
            </Col>
          </Row>
        )}
        {!userDetails?.user?.isOwner &&
          organizationStatus === UserStatus.BLOCKED_DUE_TO_PAYMENT && (
            <Row>
              <Col md="12" className="d-flex flex-column align-items-center">
                <p className="fw-bold">Blocked due to payment</p>
              </Col>
            </Row>
          )}
        <div className="form-horizontal">
          <Row>
            <Col md="4">
              <FormInputField name="firstName" label="First Name" required />
            </Col>
            <Col md="4">
              <FormInputField name="lastName" label="Last Name" required />
            </Col>
            <Col md="4">
              <FormInputField name="email" label="Email" type="email" required />
            </Col>
          </Row>
          <Row>
            <Col md="4">
              <FormInputField name="phone" label="Phone" required />
            </Col>
            <Col md="4">
              <FormInputField name="dealer" label="Dealer" disabled />
            </Col>
            <Col md="4">
              <FormSelectField
                name="status"
                label="Status"
                options={dealerUserStatuses}
                disabled={
                  userStatus === UserStatus.REJECTED ||
                  userStatus === UserStatus.BLOCKED_DUE_TO_PAYMENT
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md="4">
              <FormSelectField
                name="location"
                label="Location Name"
                options={locationsData}
                disabled={userDetails?.user?.isOwner}
              />
            </Col>
            <Col md="4">
              <FormSelectField
                name="position"
                label="Position"
                options={dealerUserPositions}
              />
            </Col>
            <Col md="4">
              <FormGroup>
                <FormLabel>Role</FormLabel>
              </FormGroup>
              <FormGroupCheckboxField
                inline
                name="role"
                checkboxes={dealerUserRoles}
                checkDependencies={checkCheckboxDependencies}
                isRoleGroupCheckbox
              />
            </Col>
          </Row>
          <Row className="mt-4 mt-md-0">
            <Col md="4">
              <FormGroup>
                <FormLabel>Marketing preferences</FormLabel>
              </FormGroup>
              <div className="d-flex gap-3">
                <FormCheckboxField name="phoneMarketingAllowed" label="Phone" />
                <FormCheckboxField name="emailMarketingAllowed" label="Email" />
                <FormCheckboxField name="smsMarketingAllowed" label="SMS" />
                <FormCheckboxField name="postMarketingAllowed" label="Post" />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md="12">
              <Button className="mt-4 float-end rounded-pill btn-primary" type="submit">
                Save
              </Button>
            </Col>
          </Row>
        </div>
      </ComponentCard>
    </Form>
  );
}

UserForm.propTypes = {
  isOwner: PropTypes.bool,
  values: PropTypes.object,
  locations: PropTypes.array,
  organizationRole: PropTypes.object,
  organizationStatus: PropTypes.string,
};
UserForm.defaultProps = {
  isOwner: false,
  values: {},
  locations: [],
  organizationRole: {},
  organizationStatus: '',
};

export default UserForm;
